import { useAllBundles } from "@customer-app/account/views/OperationAquarius/AquariusInner/hooks/useCurrentBundle";
import { useUnavailableVariantsAndDeliveries } from "@customer-app/account/views/OperationAquarius/AquariusInner/hooks/useUnavailableVariantsAndDeliveries";
import { getGift, useOrganization } from "@customer-app/account/views/OperationAquarius/utils";
import { useSellingPlanGroupForSellingPlanId } from "@customer-app/state/reducers/sellingPlanGroups";
import { useSmartrrCustomerSelector } from "@customer-app/state/typedCustomerReduxHooks";
import { useSendOrderNowAvailability } from "@customer-app/utils/useSendOrderNowAvailability";
import { IGift } from "@smartrr/shared/entities/Gift";
import {
  IPurchaseState,
  IUnavailableVariantsAndDeliveries,
  PurchaseStateAndDelivery,
} from "@smartrr/shared/entities/PurchaseState";
import { ISmartrrBundleConfig, ISmartrrSellingPlan } from "@smartrr/shared/entities/SellingPlanGroup";
import { getBundleByBundleId } from "@smartrr/shared/utils/bundles";
import { shopifyGidToNumber } from "@smartrr/shared/utils/ensureShopifyGid";
import { getNextActualDeliveryForCpsGroup } from "@smartrr/shared/utils/getScheduledDeliveries";
import { isCPSPrepaid } from "@smartrr/shared/utils/isPrepaid";
import { getStLineItemsVariantsIds } from "@smartrr/shared/utils/variants";
import { useMemo } from "react";

export interface BasicSubscriptionInfo {
  allBundles: ISmartrrBundleConfig[];
  bundle: ISmartrrBundleConfig | undefined;
  canBeCancelled: boolean;
  changeFreqAvailable: boolean;
  containsLineItemsVariants: boolean;
  containsOOSItems: boolean;
  gift: IGift | undefined;
  isDelayDateAvailable: boolean;
  isPaused: boolean;
  isPrepaid: boolean;
  isPurchaseStatesInitialized: boolean;
  isSellingPlanDeleted: boolean;
  isSellingPlanGroupsLoading: boolean;
  isSendNowAvailable: boolean;
  isSkipped: boolean;
  nonHiddenSellingPlanOptions: ISmartrrSellingPlan[];
  purchaseState: IPurchaseState;
  sellingPlanId: string | undefined;
  sellingPlanOptions: ISmartrrSellingPlan[];
  setDateAvailable: boolean;
  skdIdx: number;
  unavailableVariantsAndDeliveries: IUnavailableVariantsAndDeliveries;
}

export const useBasicSubscriptionInfo = (delivery: PurchaseStateAndDelivery): BasicSubscriptionInfo => {
  const { purchaseState, isSkipped, indexFromScheduleStart: skdIdx, date } = delivery;
  const { sellingPlanId, schedule, purchaseStateStatus, stLineItems } = purchaseState;

  const organization = useOrganization();

  const sellingPlanIdNumber = sellingPlanId === null ? undefined : shopifyGidToNumber(sellingPlanId!);

  const sellingPlanOptions = useSellingPlanGroupForSellingPlanId(sellingPlanIdNumber);
  const sellingPlanGroupList = useSmartrrCustomerSelector(state => state.sellingPlanGroups?.sellingPlanGroupList);

  const isSellingPlanDeleted = !useSellingPlanGroupForSellingPlanId(sellingPlanIdNumber).length;

  const isPurchaseStatesInitialized = useSmartrrCustomerSelector(state => state.purchaseStates.isInitialized);
  const isSellingPlanGroupsLoading = useSmartrrCustomerSelector(
    state => state.sellingPlanGroups.sellingPlansIsLoading
  );

  const isSendNowAvailable = useSendOrderNowAvailability(delivery);

  const { allBundles } = useAllBundles();

  const { unavailableVariantsAndDeliveries } = useUnavailableVariantsAndDeliveries();

  return useMemo(() => {
    const hiddenSellingPlanString = "{{H}}";
    const nonHiddenSellingPlanOptions = sellingPlanOptions.filter(
      sellingPlan =>
        !sellingPlan.name.includes(hiddenSellingPlanString) &&
        sellingPlan.shopifyNumericId !== sellingPlanIdNumber
    );

    const isPrepaid = isCPSPrepaid(purchaseState.schedule);
    const isPaused = purchaseStateStatus === "PAUSED";

    const isDelayDateAvailable =
      date.toDateString() ===
      getNextActualDeliveryForCpsGroup(organization!, [purchaseState])?.[0].toDateString();

    const changeFreqAvailable = Boolean(nonHiddenSellingPlanOptions.length) && !isPrepaid;

    const setDateAvailable = !isPrepaid && !isPaused;

    const minCycles = schedule.minCycles ?? 0;
    const totalBillingCycles = schedule.totalBillingCycles ?? 0;
    const canBeCancelled = !minCycles || (Boolean(totalBillingCycles) && totalBillingCycles >= minCycles);

    const gift = getGift(purchaseState, skdIdx);

    const bundle = getBundleByBundleId(allBundles, purchaseState);

    const variantIdsForPurchaseState = sellingPlanId
      ? sellingPlanGroupList.find(group => group.sellingPlans.find(sp => sp.shopifyId === sellingPlanId))
          ?.variantIds || []
      : [];

    const purchaseStateWithNoAddons = {
      ...purchaseState,
      stLineItems: stLineItems.filter(item => !item.isAddOn),
    };

    // Checking if product has been removed from subscription program
    const containsLineItemsVariants = getStLineItemsVariantsIds(purchaseStateWithNoAddons).every(id =>
      variantIdsForPurchaseState.includes(id)
    );

    const containsOOSItems = unavailableVariantsAndDeliveries.vnts.some(oosItem =>
      stLineItems.some(lineItem => oosItem.id === lineItem.vnt.id)
    );

    return {
      allBundles,
      bundle,
      canBeCancelled,
      changeFreqAvailable,
      containsLineItemsVariants,
      containsOOSItems,
      gift,
      isDelayDateAvailable,
      isPaused,
      isPrepaid,
      isPurchaseStatesInitialized,
      isSellingPlanDeleted,
      isSellingPlanGroupsLoading,
      isSendNowAvailable,
      isSkipped,
      nonHiddenSellingPlanOptions,
      purchaseState,
      sellingPlanId,
      sellingPlanOptions,
      setDateAvailable,
      skdIdx,
      unavailableVariantsAndDeliveries,
    };
  }, [delivery, purchaseState, schedule, isSellingPlanDeleted]);
};
